import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { CalendarCheck, Users, PartyPopper, Phone, Ship, Car, BusFront, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { Field, TextInput, TextArea, SelectInput } from "./Field";
import { api, apiErrorMessage } from "@/lib/api";

const today = () => new Date().toISOString().split("T")[0];
const empty = { name: "", email: "", phone: "", party_size: 2, date: today(), time: "", arrival: "ferry", notes: "" };

// Half-hour slots across opening hours (9:30am–8pm) for guests arriving on-site.
const ONSITE_SLOTS = (() => {
  const out = [];
  for (let m = 9 * 60 + 30; m <= 20 * 60; m += 30) {
    const h = Math.floor(m / 60), mm = m % 60;
    const ap = h >= 12 ? "pm" : "am";
    let hh = h % 12; if (hh === 0) hh = 12;
    out.push(`${hh}:${String(mm).padStart(2, "0")} ${ap}`);
  }
  return out;
})();

export default function TableBooking({ onOpenVan }) {
  const [form, setForm] = useState(empty);
  const [times, setTimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const loadTimes = useCallback(async (date) => {
    try {
      const { data } = await api.get(`/ferry/times?date=${date}`);
      setTimes(data.times);
      setForm((f) => (f.arrival === "ferry" ? { ...f, time: data.times[0] || "" } : f));
    } catch { setTimes([]); }
  }, []);

  useEffect(() => { loadTimes(form.date); /* eslint-disable-next-line */ }, [form.date]);

  const timeOptions = form.arrival === "ferry" ? times : ONSITE_SLOTS;

  const setArrival = (a) =>
    setForm((f) => ({ ...f, arrival: a, time: a === "ferry" ? (times[0] || "") : ONSITE_SLOTS[0] }));

  const goVan = () => onOpenVan?.();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.time) return toast.error("Please pick an arrival time.");
    setLoading(true);
    try {
      await api.post("/bookings/table", { ...form, party_size: Number(form.party_size) });
      setDone(true);
      toast.success("Booking request sent — we'll confirm shortly!");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <section id="book-table" className="bg-muted border-y-2 border-foreground py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <Reveal className="lg:col-span-5">
          <p className="overline text-secondary flex items-center gap-2"><CalendarCheck size={14} /> Reservations</p>
          <h2 className="mt-3 font-head font-black tracking-tight leading-[0.95] text-4xl md:text-6xl">Book a table</h2>
          <p className="mt-5 text-foreground/75 md:text-lg max-w-md">
            Tell us the day, your party and how you're arriving. Every request comes straight
            to our team to approve — if we can't seat you at that time, we'll call or email you.
          </p>
          <div className="mt-8 space-y-3 font-mono-plex text-sm">
            <p className="flex items-center gap-3"><Users size={16} className="text-secondary" /> Parties of all sizes welcome</p>
            <p className="flex items-center gap-3"><Phone size={16} className="text-secondary" /> We use your details only if a table's tight</p>
            <p className="flex items-center gap-3"><PartyPopper size={16} className="text-secondary" /> No minimum spend — just come as you are</p>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7 w-full">
          <div className="bg-background border-2 border-foreground rounded-2xl shadow-brutal-lg p-6 md:p-8">
            {done ? (
              <div className="text-center py-10" data-testid="table-booking-success">
                <PartyPopper className="mx-auto text-secondary" size={40} />
                <h3 className="mt-4 font-head font-extrabold text-2xl">Request received!</h3>
                <p className="mt-2 text-foreground/70 max-w-sm mx-auto">
                  Thanks {form.name.split(" ")[0]} — your table request for {form.party_size} on {form.date} at {form.time}
                  {" "}is with our team. We'll be in touch to confirm.
                </p>
                <button
                  onClick={() => { setForm(empty); setDone(false); }}
                  data-testid="table-booking-reset"
                  className="mt-6 font-head font-bold bg-foreground text-background px-6 py-3 rounded-full hover:bg-primary hover:text-foreground transition-colors"
                >
                  Make another booking
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="table-booking-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Your name"><TextInput data-testid="table-name" required value={form.name} onChange={set("name")} placeholder="Jane Cook" /></Field>
                  <Field label="Party size"><TextInput data-testid="table-party" type="number" min={1} max={40} required value={form.party_size} onChange={set("party_size")} /></Field>
                </div>

                <Field label="How are you arriving?">
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button" data-testid="arrival-ferry" onClick={() => setArrival("ferry")}
                      className={`flex items-center justify-center gap-2 font-head font-bold py-3 rounded-lg border-2 border-foreground transition-colors ${form.arrival === "ferry" ? "bg-secondary text-secondary-foreground" : "bg-background hover:bg-muted"}`}>
                      <Ship size={16} /> By ferry
                    </button>
                    <button type="button" data-testid="arrival-onsite" onClick={() => setArrival("onsite")}
                      className={`flex items-center justify-center gap-2 font-head font-bold py-3 rounded-lg border-2 border-foreground transition-colors ${form.arrival === "onsite" ? "bg-secondary text-secondary-foreground" : "bg-background hover:bg-muted"}`}>
                      <Car size={16} /> Arriving on-site
                    </button>
                  </div>
                </Field>

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Date"><TextInput data-testid="table-date" type="date" min={today()} required value={form.date} onChange={set("date")} /></Field>
                  <Field label={form.arrival === "ferry" ? "Ferry arrival time" : "Arrival time"}>
                    <SelectInput data-testid="table-time" value={form.time} onChange={set("time")} required>
                      {timeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                    </SelectInput>
                  </Field>
                </div>

                {form.arrival === "ferry" && (
                  <button type="button" onClick={goVan} data-testid="table-van-cta"
                    className="w-full flex items-center justify-between gap-3 bg-primary/20 border-2 border-foreground rounded-xl px-5 py-4 text-left hover:bg-primary/40 transition-colors">
                    <span className="flex items-center gap-3">
                      <BusFront size={20} />
                      <span className="font-head font-bold">Getting the ferry? Book a lift up the hill.</span>
                    </span>
                    <ArrowRight size={18} />
                  </button>
                )}

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Phone" hint="In case a table isn't free"><TextInput data-testid="table-phone" required value={form.phone} onChange={set("phone")} placeholder="021 123 4567" /></Field>
                  <Field label="Email" hint="We'll email if we can't seat you"><TextInput data-testid="table-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" /></Field>
                </div>
                <Field label="Anything else?"><TextArea data-testid="table-notes" value={form.notes} onChange={set("notes")} placeholder="High chair, celebration, dietary needs…" /></Field>
                <button
                  type="submit" disabled={loading} data-testid="table-submit"
                  className="w-full font-head font-bold text-lg bg-secondary text-secondary-foreground border-2 border-foreground rounded-full py-4 shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Request booking"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
